import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Menu
} from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from '@mui/icons-material/Block';

import styles from '../InviteCustomerModal/styles.module.scss';
import '../InviteCustomerModal/styles.global.scss';
import ValidatedTextInput from '../ValidatedTextInput';
import PhoneInput from '../PhoneInput';
import { EMAIL_REGEX } from '@src/vars';
import RadioButtonGroup from '../RadioButtonGroup';
import { ReactComponent as InvitationSentImage } from "@common/components/InvitationSentConfirmationModal/invitation-sent.svg";
import { getFriendReferrals, inviteCustomer } from "@user/sagas";
import { displayErrorMessage } from "@utils/displayErrorMessage";
import { format } from "date-fns";
import {isFunc} from "@utils/functions";
import {setReferralsNumber} from "@common/reducers";


const MODES = {
  INSTRUCTIONS: 'INSTRUCTIONS',
  INVITE: 'INVITE',
  SENT: 'SENT',
  LIST: 'LIST'
};

const TABS = [
  'NEW REFERRAL',
  'TRACK REFERRALS'
];

const ReferFriendModal = ({ open, setOpen, retrieveOnMount = false, openTable = false }) => {
  const dimensions = useSelector(state => state.dimensions);
  const { user, selectedUser } = useSelector(state => state.user);
  const { configParameters } = useSelector(state => state.configParameters);
  const { rowsPerPage } = useSelector(state => state.pagination);
  const dispatch = useDispatch();

  const [isValid, setIsValid] = useState(false);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');

  const [inviteType, setInviteType] = useState('Email');//Email|SMS
  //const [link, setLink] = useState(undefined);//eslint-disable-line
  //const [shortLink, setShortLink] = useState(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useState(MODES.INSTRUCTIONS);
  const [isAgreed, setIsAgreed] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [friendReferrals, setFriendReferrals] = useState(undefined);
  const [selectedTab, setSelectedTab] = useState('NEW REFERRAL');
  const [actionReferralId, setActionReferralId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [currentReferral, setCurrentReferral] = useState(undefined);

  useEffect(() => {
    if (!!retrieveOnMount) retrieveFriendReferrals(1);
  }, [retrieveOnMount, selectedUser]); //eslint-disable-line

  useEffect(() => {
    if (!!open) retrieveFriendReferrals(1);
  }, [rowsPerPage, open]); //eslint-disable-line

  useEffect(() => {
    if (!!open && !!openTable) setSelectedTab('TRACK REFERRALS');
  }, [openTable, open]); //eslint-disable-line

  useEffect(() => {
    setIsValid(checkIfDataIsValid());
  }, [name, surname, mobile, email, inviteType]); //eslint-disable-line

  useEffect(() => {
    if (!open) {
      setName('');
      setSurname('');
      setMobile('');
      setEmail('');
      setIsAgreed(false);
      setMode(MODES.INSTRUCTIONS);
      setSelectedTab('NEW REFERRAL');
    }
  }, [open]); //eslint-disable-line

  useEffect(() => {
    if (!!selectedTab) {
      setMode(selectedTab === 'NEW REFERRAL' ? MODES.INSTRUCTIONS : MODES.LIST);
    }
  }, [selectedTab]); //eslint-disable-line

  const retrieveFriendReferrals = (page = 1) => {
    if (!!user) {
      setIsLoading(true);
      if (currentPage !== page) setCurrentPage(page);
      let options = {
        limit: rowsPerPage,
        page,
        onSuccess: (data) => {
          dispatch(setReferralsNumber(data.total ?? 0));
          setFriendReferrals(data);
          setIsLoading(false);
        },
        onError: (data) => {
          setIsLoading(false);
          displayErrorMessage(data, 'Unable to retrieve friend referrals');
        }
      };

      if (!!selectedUser) {
        options.userId = selectedUser?.id;
      }

      dispatch(getFriendReferrals(options));
    }
  };

  const inviteTypeOptions = [
    { value: 'Email' },
    { value: 'SMS' }
  ];

  const checkIfDataIsValid = () => {
    return (
        !!name && (name.trim()).length >= 2 && name.length <= 200 && /^[A-Za-z ]*$/.test(name)
        && !!surname && (surname.trim()).length >= 2 && surname.length <= 200 && /^[A-Za-z ]*$/.test(surname)
        && (inviteType !== 'SMS' || validatePhone())
        && ((EMAIL_REGEX.test(email) && email.length <= 200) || inviteType !== 'Email')
        && ['Email', 'SMS'].includes(inviteType)
      );
  };

  const validatePhone = () => {
    return /^0?\({0,1}(4|5){1}\){0,1}( |-){0,1}[0-9]{2}(\|){0,1}[0-9]{2}(\|){0,1}[0-9]{1}( |-){0,1}[0-9]{3}$/gm.test(mobile ?? '');
  };

  const onIsAgreedChange = () => {
    setIsAgreed(!isAgreed);
  };

  const doInviteCustomer = (body) => {
    dispatch(inviteCustomer({
      body: {
        ...body,
        mobile: body.mobile ? (body.mobile.substr(0, 1) === '0' ? `${body.mobile}` : `0${body.mobile}`) : undefined,
      },
      onSuccess: () => {
        if (mode !== MODES.LIST) setMode(MODES.SENT);
        retrieveFriendReferrals(1);
        setIsProcessing(false);
      },
      onError: (data) => {
        displayErrorMessage(data, 'Unable to send an invite');
        setIsProcessing(false);
      }
    }));
  };

  const handleMenuOpen = (event, referral) => {
    if (menuPosition) return;

    event.preventDefault();
    setCurrentReferral(referral);
    setActionReferralId(referral.id);
    setMenuPosition({
      top: event.clientY + 12,
      left: event.clientX + 12
    });
  };

  const doReInvite = (referral) => {
    setIsProcessing(true);
    const isMobile = !!referral.invitee?.mobile;
    const body = {
      name: referral.invitee?.name,
      surname: referral.invitee?.surname,
      mobile: !!isMobile ? referral.invitee.mobile : undefined,
      email: !isMobile ? referral.invitee.email : undefined,
      deliveryChannel: isMobile ? 'SMS' : 'Email',
      invitedByFriend: user?.id,
    };

    doInviteCustomer(body);
  };

  const renderMenu = (referral) => {
    return (
      <Menu
        open={!!menuPosition && (referral?.id) === actionReferralId}
        onClose={() => { setMenuPosition(null); setActionReferralId(null) }}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
      >
        <MenuItem
          onClick={(e) => { e.preventDefault(); setMenuPosition(null); doReInvite(referral); }}
          disabled={!!referral.isSignedUp || !!isProcessing || !!isLoading || (!referral.invitee?.mobile && !referral.invitee?.email)}
        >
          RE INVITE
        </MenuItem>
      </Menu>
    );
  };

  const renderFAQBlock = () => {
    return (<>
      <Box sx={{ mb: 4 }} className={styles.vertSpace}/>
      <Grid item container xs={12} alignItems="flex-start" alignContent="flex-start" justifyContent={'flex-start'}>
        <Grid item className={styles.textWrapper}><b>Want to learn more?</b> <a href={configParameters?.friendRefFaqUrl} className={styles.textLink}>Read our FAQ</a></Grid>
      </Grid>
    </>);
  }

  return (
    <>
      <Dialog open={open} className={'ReferFriendDialog'} onClose={() => {}} fullScreen={dimensions.isScreenLessSM}>
        <MuiDialogTitle>
          <Box className={`${styles.modalTitle} ${dimensions.isScreenLessMD ? styles.mobile : ''}`} data-testid="refer-friend-dialog-title">
            {mode === MODES.SENT ? 'Invitation sent' : 'Refer a friend'}
          </Box>
          <IconButton aria-label="close" className={styles.closeButton} disabled={!!isProcessing} onClick={() => setOpen(false)} data-testid="button-refer-friend-close">
            <CloseRoundedIcon />
          </IconButton>
        </MuiDialogTitle>
        <MuiDialogContent>
          <Grid container justifyContent="center">
            <Grid item className={`${styles.contentContainer}`}>
              {dimensions.isScreenBiggerXS ? (
                <Grid container alignItems={"flex-start"} direction={"row"} justifyContent={"flex-start"}>
                  {TABS.map((tab, idx) => { return (
                    <Grid
                      item
                      container
                      direction={"column"}
                      alignItems={"center"}
                      className={`${styles.tabItem} ${styles.twoItems} ${tab === selectedTab ? styles.selected : ''}`}
                      onClick={() => { if (tab !== selectedTab) setSelectedTab(tab); }}
                      key={idx}
                    >
                      <Grid item>{tab}</Grid>
                    </Grid>
                  ); })}
                </Grid>
              ) : (
                <FormControl variant="outlined" className={styles.selectWrapper}>
                  <Select value={selectedTab}>
                    {TABS.map((tab, idx) => {
                      return (
                        <MenuItem
                          value={tab}
                          key={idx}
                          onClick={() => { if (tab !== selectedTab) setSelectedTab(tab); }}
                        >
                          <div style={{ textTransform: "uppercase" }}>{tab}</div>
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ mb: 2 }} className={styles.vertSpace}/>

              {mode === MODES.INSTRUCTIONS && (<>
                <Grid container spacing={2} alignItems="flex-start" alignContent="flex-start">
                  <Grid item xs={12} className={styles.text}>
                    Invite a friend to start their home loan journey with Domium and earn <span className={styles.subheader}>{configParameters?.friendRefReward}</span> once your friend’s loan settles.
                  </Grid>
                  <Box sx={{ mb: 2 }} className={styles.vertSpace}/>
                  <Grid item xs={12} className={styles.subheader}>
                    How it works
                  </Grid>
                  <Box sx={{ mb: 1 }} className={styles.vertSpace}/>
                  <Grid item container xs={12} alignItems="flex-start" alignContent="flex-start" justifyContent={'flex-start'}>
                    <Grid item className={styles.pointNumberWrapper}>1</Grid>
                    <Grid item className={styles.textWrapper}>Provide your friend’s email address or phone number</Grid>
                  </Grid>
                  <Grid item container xs={12} alignItems="flex-start" alignContent="flex-start" justifyContent={'flex-start'}>
                    <Grid item className={styles.pointNumberWrapper}>2</Grid>
                    <Grid item className={styles.textWrapper}>We’ll send them an email or SMS with unique sign-up link</Grid>
                  </Grid>
                  <Grid item container xs={12} alignItems="flex-start" alignContent="flex-start" justifyContent={'flex-start'}>
                    <Grid item className={styles.pointNumberWrapper}>3</Grid>
                    <Grid item className={styles.textWrapper}>When their <b>loan settles</b>, we’ll email you instructions to claim your {configParameters?.friendRefReward} reward.</Grid>
                  </Grid>
                  {!dimensions.isScreenLessSM && renderFAQBlock()}
                  <Box sx={{ mb: 4 }} className={styles.vertSpace}/>
                  <Grid item container xs={12} alignItems="flex-start" alignContent="flex-start" justifyContent={'space-between'}>
                    <Grid item className={styles.switcherWrapper}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isAgreed}
                            inputProps={{ 'data-testid': 'switch-refer-friend-terms' }}
                            onChange={onIsAgreedChange}
                            name="isAgreed"
                            color="primary"
                            disabled={!!isProcessing}
                          />
                        }
                        label={''}
                      />
                    </Grid>
                    <Grid item className={styles.textWrapper}>I acknowledge that I have read and agree to the <a href={configParameters?.referralTermsAndConditionsUrl} className={styles.textLink}>Referral Terms and Conditions</a>.</Grid>
                  </Grid>
                  {!!dimensions.isScreenLessSM && renderFAQBlock()}
                </Grid>
              </>)}

              {mode === MODES.INVITE && (<>
                <Grid
                  container
                  spacing={4}
                  alignItems="flex-start"
                  alignContent="flex-start"
                >
                  <Grid item xs={12} md={6} data-testid='customer-name-input-block'>
                    <Grid className={styles.info}>Name</Grid>
                    <ValidatedTextInput
                      placeholder=""
                      value={name || ''}
                      data-testid="input-refer-friend-name"
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      name="name"
                      validators={['minStringLength:1', 'minStringLength:2', 'maxStringLength:200', 'matchRegexp:^[A-Za-z ]*$']}
                      errorMessages={[
                        'Name is required',
                        'Must be at least 2 characters',
                        'You have exceeded the maximum number of 200 characters',
                        'Must contain only letters and spaces'
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} data-testid='customer-surname-input-block'>
                    <Grid className={styles.info}>Surname</Grid>
                    <ValidatedTextInput
                      placeholder=""
                      value={surname || ''}
                      data-testid="input-refer-friend-surname"
                      onChange={(e) => setSurname(e.target.value)}
                      fullWidth
                      variant="outlined"
                      name="surname"
                      validators={['minStringLength:1', 'minStringLength:2', 'maxStringLength:200', 'matchRegexp:^[A-Za-z ]*$']}
                      errorMessages={[
                        'Surname is required',
                        'Must be at least 2 characters',
                        'You have exceeded the maximum number of 200 characters',
                        'Must contain only letters and spaces'
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid className={styles.info}>How would you like to send the invite?</Grid>
                    <Grid container direction="row" justifyContent="space-between">
                      <Grid data-testid='customer-invite-type-selector-block' item xs={12}>
                        <RadioButtonGroup
                          onChange={(v) => {
                            setInviteType(v);
                          }}
                          groupTestId="radio-refer-friend-invite-type"
                          options={inviteTypeOptions}
                          defaultValue={inviteType}
                          forceFullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {inviteType === 'SMS' && (
                    <Grid item xs={12} data-testid='customer-mobile-input-block'>
                      <Grid className={styles.info}>Mobile number</Grid>
                      <Grid container direction="row" justifyContent="space-between">
                        <PhoneInput
                          onChange={e => setMobile(e.target.value)}
                          inputTestId="input-refer-friend-mobile"
                          value={mobile}
                          fullWidth
                        />
                        <Grid item xs={12} className={styles.caption}>Customer will receive an invitation to this number</Grid>
                      </Grid>
                    </Grid>
                  )}

                  {inviteType === 'Email' && (
                    <Grid item xs={12} data-testid='customer-email-input-block'>
                      <Grid className={styles.info}>Email</Grid>
                      <ValidatedTextInput
                        placeholder=""
                        value={email ?? ''}
                        data-testid="input-refer-friend-email"
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        variant="outlined"
                        name="email"
                        validators={['maxStringLength:200', 'isEmail']}
                        errorMessages={['You have exceeded the maximum number of 200 characters', 'Incorrect email format']}
                      />
                      <Grid item xs={12} className={styles.caption}>Customer will receive an invitation to this email</Grid>
                    </Grid>
                  )}
                </Grid>
              </>)}

              {mode === MODES.SENT && (
                <Grid
                  container
                  direction="column"
                  spacing={1}
                  justifyContent="space-between"
                  alignItems="center"
                  alignContent="center"
                >
                  <Grid item className={styles.imageWrapper}>
                    <InvitationSentImage />
                  </Grid>
                  <Grid item container className={styles.textWrapper} xs={11} spacing={3} justifyContent={'center'}>
                    <Grid item className={styles.centeredText}>Your invite to <b>{name}</b> is on its way.</Grid>
                    <Grid item className={styles.centeredText}>Thanks for sharing the Domium experience!</Grid>
                  </Grid>
                </Grid>
              )}

              {mode === MODES.LIST && (<>
                <Grid
                  container
                  spacing={2}
                  alignItems="flex-start"
                  alignContent="flex-start"
                >
                  <Grid item>
                    <TableContainer className={`${styles.tableContainer} ${styles.fixedWidth} FriendReferralsTableWrapper`}>
                      <Table size={dimensions.isScreenBiggerSM ? "medium" : "small"} stickyHeader>
                        <TableHead>
                          <TableRow className={styles.tableHead}>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 130 }}
                            >
                              NAME
                            </TableCell>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 100 }}
                            >
                              LAST INVITE
                            </TableCell>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 64 }}
                            >
                              SIGNED-UP
                            </TableCell>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 64 }}
                            >
                              SETTLED
                            </TableCell>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 64 }}
                            >
                              FEE PAID
                            </TableCell>
                            <TableCell
                              align={'left'}
                              style={{ minWidth: 10, width: 10 }}
                            />
                          </TableRow>
                        </TableHead>
                        <TableBody className={styles.tableBody}>
                          {(friendReferrals?.elements ?? []).map((referral, idx) => {

                            return (
                              <TableRow className={styles.tableItem} key={idx}>
                                <TableCell
                                  align={'left'}
                                >
                                  <div className={`${styles.username}`}>
                                    {referral.invitee?.name} {referral.invitee?.surname}
                                  </div>
                                  <div className={`${styles.caption}`}>
                                    {referral.invitee?.mobile ?? referral.invitee?.email}
                                  </div>
                                </TableCell>
                                <TableCell
                                  align={'left'}
                                  className={`${styles.username}`}
                                >
                                  <span>{referral.updatedAt ? format(new Date(referral.updatedAt), 'dd MMM yy').toUpperCase() : '???'}</span>
                                </TableCell>
                                <TableCell align={'left'} className={styles.username}>
                                  {!!referral.isSignedUp ? <CheckCircleIcon className={styles.checkIcon} /> : <BlockIcon className={styles.blockIcon} />}
                                </TableCell>
                                <TableCell align={'left'} className={styles.username}>
                                  {!!referral.isSettled ? <CheckCircleIcon className={styles.checkIcon} /> : <BlockIcon className={styles.blockIcon} />}
                                </TableCell>
                                <TableCell align={'left'} className={styles.username}>
                                  {!!referral.isRewardPayed ? <CheckCircleIcon className={styles.checkIcon} /> : <BlockIcon className={styles.blockIcon} />}
                                </TableCell>
                                <TableCell align={'left'}>
                                  <IconButton
                                    className={`${styles.iconBtn} ${styles.noPadding}`}
                                    onClick={(event) => { handleMenuOpen(event, referral); }}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                  {renderMenu(referral)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>

                      {(!friendReferrals?.elements?.length) && (
                        <Grid container className={styles.emptyTable} justifyContent={"center"} alignItems={"center"}>
                          <Grid item>{isLoading ? 'Loading...' : 'No referrals found'}</Grid>
                        </Grid>
                      )}
                      {(isLoading || isProcessing) && (<CircularProgress size={80} className={styles.cardProgress} />)}
                    </TableContainer>
                  </Grid>
                </Grid>
              </>)}
            </Grid>
          </Grid>
        </MuiDialogContent>
        <MuiDialogActions>
          {mode === MODES.INSTRUCTIONS && (
            <Grid container justifyContent="space-between">
              <Button
                className={`${styles.actionButton} ${styles.secondary}`}
                onClick={() => setOpen(false)}
                data-testid="button-refer-friend-cancel"
                color="secondary"
                variant="outlined"
                type="button"
                disableElevation
              >
                CANCEL
              </Button>
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={!isAgreed}
                onClick={() => setMode(MODES.INVITE)}
                data-testid="button-refer-friend-continue"
              >
                CONTINUE
              </Button>
            </Grid>
          )}

          {mode === MODES.INVITE && (
            <Grid container justifyContent="space-between">
              <Button
                className={`${styles.actionButton} ${styles.secondary}`}
                onClick={() => setMode(MODES.INSTRUCTIONS)}
                data-testid="button-refer-friend-back"
                color="secondary"
                variant="outlined"
                type="button"
                disableElevation
              >
                BACK
              </Button>
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={!isValid || isProcessing}
                onClick={() => {
                  setIsProcessing(true);
                  doInviteCustomer({
                    name: name.trim(),
                    surname: surname.trim(),
                    mobile: inviteType === 'SMS' ? mobile : undefined,
                    email: inviteType === 'Email' ? email : undefined,
                    deliveryChannel: inviteType,
                    invitedByFriend: user?.id,
                  });
                }}
                data-testid="button-refer-friend-invite"
              >
                {!!isProcessing && <CircularProgress size={14} className={styles.buttonProgress} />}INVITE
              </Button>
            </Grid>
          )}

          {mode === MODES.SENT && (
            <Grid container justifyContent="space-between">
              <div />
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={!isValid || isProcessing}
                onClick={() => { setOpen(false); }}
                data-testid="button-refer-friend-close-sent"
              >
                CLOSE
              </Button>
            </Grid>
          )}

          {mode === MODES.LIST && (
            <Grid container justifyContent="space-between">
              <div />
              <Button
                className={`${styles.actionButton}`}
                type="button"
                variant="contained"
                color="primary"
                disabled={isProcessing}
                onClick={() => { setOpen(false); }}
              >
                CLOSE
              </Button>
            </Grid>
          )}
        </MuiDialogActions>
      </Dialog>
    </>
  );
};

ReferFriendModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired
};

export default ReferFriendModal;


